// THE SOURCE LOCALE. Every other locale's dictionary has this shape (`Dict`),
// and the CI gate (tools/ci/i18n-parity.ts) holds every locale to its key set.
//
// The runtime bundles this module eagerly: English is the fallback for any key
// a locale lacks, so it must be present before the first paint. Every other
// locale is a module of its own under src/locales/, which src/i18n.ts loads
// when that locale is chosen (D-430).

import type { Translated } from "../i18n-shape.js";
import { catalogueEn } from "../areas/office/catalogue/catalogue.i18n.js";
import { inventoryEn } from "../areas/office/inventory/inventory.i18n.js";
import { wasteEn } from "../areas/office/waste/waste.i18n.js";
import { productionEn } from "../areas/office/production/production.i18n.js";
import { onboardingEn } from "../areas/onboarding/onboarding.i18n.js";
import { qualityEn } from "../areas/office/quality/quality.i18n.js";
import { salesEn } from "../areas/office/sales/sales.i18n.js";
import { messagingEn } from "../areas/office/messaging/messaging.i18n.js";
import { publicEn } from "../areas/public/public.i18n.js";
import { auditEn } from "../areas/office/audit/audit.i18n.js";
import { adminEn } from "../areas/admin/admin.i18n.js";
import { viewsEn } from "../areas/office/views/views.i18n.js";
import { planningEn } from "../areas/office/planning/planning.i18n.js";
import { agentsEn } from "../areas/office/agents/agents.i18n.js";

export const en = {
  app: { title: "Assessly Factory OS" },
  nav: {
    catalogue: "Catalogue",
    // The onboarding wizard had no entry point in the product at all before
    // these — the whole module was URL-only (spec 02 §4 S1/S9).
    onboarding: "Set up",
    terminology: "Terminology",
    industryPack: "Industry pack",
    inventory: "Inventory",
    // Module 05 §4: without a nav entry the settings screen is URL-only, and a
    // group_admin has no link to follow.
    waste: "Waste",
    // Module 06 §4 floor rows: without an entry the floor slice is URL-only,
    // and a floor_worker has no other link they are permitted to follow.
    floor: "Floor",
    // Module 04 §4 (slice 04-A): the office production area — S1 list, S2
    // detail, S4 dispatch. Gated on `production.read` in routes/root.tsx.
    production: "Production",
    // Module 07 §4 (slice 07-A): the office quality area — the inspection
    // queue and the control-point library. Gated on `qualityAreaEntries`.
    quality: "Quality",
    // Module 09 §4 (slice P3-A part 2): the office sales area — the customer
    // book, the 360 and the price-list manager. Gated on `salesAreaEntries`.
    sales: "Sales",
    // Module 17 §4 (slice P3-B part 2): the office messaging area — the
    // conversations inbox, the template catalog and the ga/fa settings
    // screen. Gated on `messagingAreaEntries`.
    messaging: "Messaging",
    // Module 03 §4 (D-354): the office planning area. Gated on
    // `planningAreaEntries` in routes/root.tsx.
    planning: "Planning",
    // The accessible name of the language control (components/locale-switcher).
    // The OPTIONS are endonyms and deliberately not dictionary keys — someone
    // who cannot read the current UI language must still find their own.
    language: "Language",
    logout: "Sign out",
    // Device hand-over (§9.2): unsent work is named before it is thrown away.
    logoutPending: "{{count}} item(s) on this device have not been sent yet.",
    logoutAnyway: "Sign out and lose them",
    staySignedIn: "Stay signed in",
    // UX-0 AppShell (design-system spec §3.1).
    home: "Home",
    brandName: "Assessly",
    brandSub: "Factory OS",
    // NOT bare "Search": several screens carry their own field named exactly
    // "Search" and the certified walkthroughs address them with
    // getByLabel("Search", { exact: true }) — a colliding accessible name on
    // the topbar breaks that stable contract (found by M04-S01 on the replica).
    searchLabel: "Global search",
    searchPlaceholder: "Search SKUs, products…",
    envStaging: "Staging",
    // Rev-7: the setup chip reads as a percentage, and the phone bar's last
    // button opens every other section.
    setupScore: "{{score}}%",
    more: "More",
    moreTitle: "All sections",
    moreClose: "Close",
    // The accessible name of every HelpTip ("?") that holds the text a screen
    // used to print as a paragraph (design-system §3.17).
    helpAbout: "About {{topic}}",
    // The shared RowMenu trigger (components/row-menu.tsx): one name per row,
    // so two rows never share it.
    moreActionsFor: "More actions for {{name}}",
    // The customer guide (docs/spec/guide.md §7): the office and floor top
    // bars' Help, the floor home's tile and the HelpTip's guide link.
    help: "Help",
    helpTile: "Help",
    helpTileSub: "How to do each job, with pictures",
    helpRead: "Read more in the guide",
  },
  // Office home (design-system spec §6 UX-0), composed by role (D-443).
  home: {
    title: "Operations",
    crumbRoot: "Home",
    updated: "Updated {{time}}",
    refresh: "Refresh",
    loading: "Loading the overview…",
    // The role cards: what the reader's day holds, as counts that open the
    // list they count.
    day: {
      production: "Production",
      openWos: "Open work orders",
      overdueWos: "Overdue work orders",
      draftWos: "Not released yet",
      planning: "Planning",
      schedulePublished: "Schedule version {{version}}",
      scheduleNone: "No schedule published yet",
      scheduleUnknown: "Schedule could not be read",
      scheduleWaiting: "New plan waiting",
      lateOrders: "Late orders",
      overloads: "Overloaded weeks",
      actionMessages: "Action messages",
      purchases: "Purchases to place",
      toRelease: "Production to release",
      sales: "Sales",
      reviews: "Open reviews",
      quotesToSend: "Approved quotes to send",
      quotesSent: "Sent, waiting for the customer",
      dealsStalling: "Deals going stale",
      warehouse: "Warehouse",
      toReceive: "Transfers to receive",
      toShip: "Orders ready to ship",
      quality: "Quality",
      inspections: "Inspections to do",
      quarantine: "In quarantine",
    },
    attention: {
      title: "Needs attention",
      colType: "Type",
      colItem: "Item",
      colDetail: "Detail",
      colAge: "Age",
      colAction: "Action",
      typeImport: "Import",
      typeSetup: "Setup",
      typeCount: "Count",
      typeFloor: "Floor",
      typeQuality: "Quality",
      importDetail: "{{failed}} of {{total}} rows rejected",
      importGroup: "{{count}} import files with rejected rows",
      importAction: "Fix rows",
      newest: "Newest: {{name}}",
      checklistDetail: "Needed before go-live",
      checklistAction: "Open step",
      checklistGroup: "{{count}} setup checks to finish",
      checklistGroupAction: "Open checklist",
      countDetail: "Count waiting for review",
      countGroup: "{{count}} counts waiting for review",
      countAction: "Review",
      conflictOne: "Floor event",
      conflictGroup: "{{count}} floor events",
      conflictUnnamed: "Floor event not applied",
      conflictAction: "Resolve",
      handoffDetail: "Defect waiting for a decision ({{qty}})",
      handoffGroup: "{{count}} defects waiting for a decision",
      handoffUnnamed: "Defects",
      handoffAction: "Decide",
      emptyTitle: "Nothing needs attention",
      emptyBody: "Imports, counts, floor events, quality hand-offs and your tasks are all clear.",
      // A reader without `sales.activity.write` holds no tasks, and the card
      // reads none for them, so their all-clear names none (spec 09 §4.16).
      emptyBodyNoTasks: "Imports, counts, floor events and quality hand-offs are all clear.",
      showAll: "Show all ({{count}})",
      showFewer: "Show fewer",
      range: "{{from}}–{{to}} of {{total}}",
      prevPage: "Previous page",
      nextPage: "Next page",
      // My day (spec 09 §4.16, D-313 ruling 8): the reader's open tasks due
      // today or earlier, as rows of this card. An overdue row is red with its
      // age, today's is amber, each completes with one press, and the footer
      // links to My day on the task list. No count of tasks is printed. A
      // reader without `sales.activity.write` sees no task rows.
      typeTask: "Task",
      taskOverdue: "Overdue {{count}} d",
      taskToday: "Today",
      taskComplete: "Complete",
      taskCompleting: "Completing…",
      taskCompleteFailed: "Could not complete the task.",
      openMyDay: "Open My day",
      // My day's read failed: never an all-clear over tasks it could not see.
      myDayFailed: "Your tasks could not be read. Open My day to see them.",
    },
    setup: {
      title: "Workspace setup",
      continue: "Continue: {{step}}",
      products: "Products in the catalogue",
      workCenters: "Work centres",
    },
    // The floor hub's "Next for you" card (D-444): the one thing to open now.
    floor: {
      nextLabel: "Next for you",
      nextTraySub: "The office could not take these. Open them first.",
      nextStation: "Back to {{name}}",
      nextStationUnnamed: "Back to your station",
      nextStationSub: "Your queue and the next job",
      nextPickStation: "Choose your station",
      nextPickStationSub: "Scan its QR or pick it from the list",
      nextCountsSub: "Open your stock counts",
      nextReceiveSub: "See what is on its way here",
      nextInspectSub: "Open the inspection queue",
      nextShipSub: "See what is ready to pick",
      shipNotYoursTitle: "Shipping is not part of your role",
      shipNotYoursBody: "Ask whoever ships orders at your site.",
      from: "From {{name}}",
      due: "Due {{date}}",
      started: "Started {{date}}",
    },
    age: {
      min: "{{count}} min",
      h: "{{count}} h",
      d: "{{count}} d",
    },
  },
  login: {
    title: "Sign in",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    error: "Sign-in failed. Check your email and password.",
    totpTitle: "Two-factor code",
    totpCode: "Authentication code",
    totpSubmit: "Verify",
    totpError: "That code was not accepted. Try again.",
    forgot: "Forgot your password?",
  },
  // The password-reset pair (UX-6a — routes/forgot-password.tsx + reset-password.tsx,
  // spec 16 §3.4). The forgot door is deliberately success-shaped for every
  // input (no user oracle), and the copy says so honestly: "if an account
  // exists" rather than pretending to confirm one.
  auth: {
    // The suspended tenant (spec 15 §3.2 and S20; D-495, D-627).
    suspension: {
      lockedTitle: "This workspace is suspended",
      lockedNotice: "This workspace is suspended.",
      lockedBilling:
        "An invoice is overdue, so Assessly has paused access. Contact Assessly to restore it.",
      lockedOther:
        "Assessly has paused access to this workspace. Contact your Assessly account manager to restore it.",
      dataKept: "Your data is kept. Nothing has been deleted.",
      graceBilling:
        "Read-only until {{date}}: an invoice is overdue. Contact Assessly to keep full access.",
      graceOther:
        "Read-only until {{date}}: Assessly has suspended this workspace. Contact your Assessly account manager.",
      closedTitle: "This workspace was closed",
      closedBody:
        "Its data was deleted at the owner's request. Contact Assessly if this is unexpected.",
      floorPaused: "Your work is kept on this device and sends once the account is restored.",
    },
    forgotTitle: "Reset your password",
    forgotLead: "Enter your account email and we will send a reset link.",
    forgotSubmit: "Send reset link",
    forgotSubmitting: "Sending…",
    forgotSent:
      "If an account exists for that email, a reset link is on its way. It expires in 30 minutes.",
    forgotError: "That did not work. Check the email address and try again.",
    backToSignIn: "Back to sign in",
    resetTitle: "Choose a new password",
    resetLead: "Set a new password for your account.",
    resetPassword: "New password",
    resetConfirm: "Repeat new password",
    resetSubmit: "Set password",
    resetSubmitting: "Setting…",
    resetMismatch: "The two passwords do not match.",
    resetInvalid: "This reset link is invalid or has expired. Request a new one.",
    resetPolicy: "That password is not accepted. Choose a longer or less common one.",
    resetError: "That did not work. Try again.",
    resetDone: "Your password is set and you are signed in.",
    resetContinue: "Continue",
    resetNoToken:
      "This page needs the link from your reset email. If yours has expired, request a new one.",
    requestNew: "Request a new link",
  },
  // The `products.*` and `newProduct.*` namespaces lived here for the P0
  // walking-skeleton list and create form. Those screens are retired (their
  // paths now redirect into `/catalogue/products`, router.tsx), and the module's
  // own strings are `catalogue.productsList.*` — so these keys went with them
  // rather than staying as four locales of dead text.
  kind: {
    finished_good: "Finished good",
    semi_finished: "Semi-finished",
    raw_material: "Raw material",
    consumable: "Consumable",
    service: "Service",
  },
  status: {
    draft: "Draft",
    active: "Active",
    discontinued: "Discontinued",
  },
  // The designed 404 (routes/not-found.tsx) — §3.9 grammar: one claim, one
  // obvious next action, instead of the router's bare `<p>Not Found</p>`.
  notFound: {
    title: "That page does not exist",
    body: "The link may be out of date, or the address may have a typo. Nothing is broken.",
    cta: "Go to Home",
  },
  // SYSTEM ROLE LABELS. The ten seeded system roles carry an i18n KEY as their
  // `name` column (`apps/api/migrations/0002_tenancy.sql`: `'group_admin',
  // 'roles.group_admin'`), so any screen that printed `role.name` printed the
  // key — the invite control on the plant map offered "roles.group_admin",
  // "roles.qc" … as its choices, on the design partner's own tenant (UX-1
  // surface review F5). Every reader goes through `roleLabel()`
  // (lib/role-label.ts), which translates the key and falls back to the name
  // itself, so a tenant's own custom role (a real human name in that column)
  // still renders as written. Exhaustiveness over the ten seeded codes is
  // gated in __tests__/i18n-dynamic-keys.test.ts.
  roles: {
    group_admin: "Group admin",
    facility_admin: "Facility admin",
    planner: "Planner",
    sales: "Sales",
    procurement: "Procurement",
    finance: "Finance",
    qc: "Quality control",
    maintenance: "Maintenance",
    storekeeper: "Storekeeper",
    floor_worker: "Floor worker",
  },
  // `components/ref-label.tsx` — the two states a reference has that are NOT a
  // name. They are app-wide (three office areas paint them) rather than any one
  // module's, so they sit beside `roles` rather than inside a namespace. Both
  // are accessible names for a one-glyph rendering: the em dash and the ellipsis
  // say nothing to a screen reader on their own.
  ref: {
    none: "Nothing recorded",
    resolving: "Looking up the name…",
    copy: "Copy {{id}}",
    copied: "Copied",
    // What `enumLabel` shows for a code its catalogue has no name for yet:
    // never the raw code (design-system §3.9).
    unlisted: "Other",
  },
  // COUNT SLOTS (routes/shell-data.ts). App-wide for the same reason as `ref`
  // and `evidence`: the sidebar nav rows and the home KPI strip both paint
  // them, and a form written inside one area is a form the next one hand-rolls
  // differently. There is no count endpoint, so every count comes from ONE page
  // of a cursor-paginated list and has three possible states.
  count: {
    // A TRUE LOWER BOUND, in the width of a nav row. The page came back with a
    // cursor, so there are at least this many rows. Rendered wherever a plain
    // number would be; it is the state a real factory's catalogue is in almost
    // all the time, so it may never look like an error.
    atLeast: "{{count}}+",
    // "We could not read this" — a DIFFERENT fact from "there are more than we
    // counted", and it has to keep looking different. Only surfaces that would
    // otherwise paint an empty value slot use it (the KPI tiles); the sidebar
    // renders nothing at all, because the chrome never reports a read failure.
    unknown: "—",
  },
  // `components/evidence-photos.tsx` — an attachment, shown. App-wide for the
  // same reason as `ref` above: modules 04 (defect evidence), 05 (waste and
  // remnant capture) and 07 (quality records) all attach files under one
  // contract, and a strip written inside one area is a strip the next two
  // hand-roll.
  // UNIT SYMBOLS (lib/quantity.ts `unitSymbol`, design-system §3.9). A unit code on the wire
  // is pack data and never reaches the glass. Each key is a pack unit code or
  // one of the QC measurement units (packages/verticals QC_MEASUREMENT_UOMS);
  // `_one` is the singular a counted unit takes after 1. Percent is the sign
  // in every language and has no key.
  unit: {
    m: "m",
    km: "km",
    kg: "kg",
    t: "t",
    mm: "mm",
    mm2: "mm²",
    pcs: "pcs",
    pcs_one: "pc",
    min: "min",
    hr: "h",
    l: "L",
    ma: "mA",
    ohm_per_km: "Ω/km",
    barrel: "barrels",
    barrel_one: "barrel",
    bobbin: "bobbins",
    bobbin_one: "bobbin",
    coil: "coils",
    coil_one: "coil",
    bag25kg: "bags (25 kg)",
    bag25kg_one: "bag (25 kg)",
    box: "boxes",
    box_one: "box",
    pallet: "pallets",
    pallet_one: "pallet",
  },
  evidence: {
    none: "No photo attached",
    resolving: "Opening the photo…",
    unavailable: "This photo cannot be shown",
    open: "Open the full-size photo",
    caption: "Photo {{n}} of {{total}}",
  },
  catalogue: catalogueEn,
  onboarding: onboardingEn,
  inventory: inventoryEn,
  waste: wasteEn,
  production: productionEn,
  quality: qualityEn,
  sales: salesEn,
  messaging: messagingEn,
  // Module 09 §4.10 (slice P3-C-1): THE PUBLIC PAGES — the tokenized quote
  // accept page and the order tracking page. The only namespace in this
  // dictionary read by somebody who does not work at the factory, which is
  // why it is its own (areas/public/public.i18n.ts states the three reasons)
  // and why `ru` here is not a convenience but a customer-facing requirement.
  public: publicEn,
  audit: auditEn,
  admin: adminEn,
  views: viewsEn,
  planning: planningEn,
  // Module 14 §4.2 to §4.4 (P3-K K-2, D-529 ruling 2): the Hub, the agent
  // detail and the action journal.
  agents: agentsEn,
};

// The dictionary SHAPE with every leaf widened to `string` (i18n-shape.ts).
// Namespaces declare their `en` object `as const`, so `typeof en` alone would
// demand that ru say the literal English words back. `Translated` keeps the KEY
// structure (missing/extra keys still fail tsc) and frees the values.
export type Dict = Translated<typeof en>;
